import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      toast.success(data.message || "Password reset. Please sign in.");
      navigate("/auth");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl border border-black/5 bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
          <KeyRound className="h-8 w-8 text-emerald-700" />
          <h1 className="mt-4 font-serif text-4xl text-emerald-950">Choose a new password</h1>
          <p className="mt-2 text-stone-500 text-sm">Enter and confirm your new password below.</p>

          {!token ? (
            <p data-testid="reset-no-token" className="mt-6 text-sm text-red-600">This reset link is missing its token. Please request a new link from the sign-in page.</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">New password</Label>
                <Input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required className="mt-1.5 rounded-xl" placeholder="••••••••" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-500">Confirm password</Label>
                <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 rounded-xl" placeholder="••••••••" />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
              <Button data-testid="reset-submit" type="submit" disabled={busy} className="w-full rounded-full h-12 bg-emerald-900 hover:bg-emerald-800 text-white">
                {busy ? "Resetting…" : "Reset password"}
              </Button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
